import { ThemeProvider } from '@mui/material';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import AuthRoute from './_components/auth/auth-route';
import './App.css';
import { ActionTypeIndex } from './pages/actionType/actionType-index';
import { FormIndex } from './pages/form/form-index';
import { FormPageManage } from './pages/form/form-pageManage';
import { FormTemplateIndex } from './pages/formTemplate/formTemplate-index';
import { FormTemplateSectionView } from './pages/formTemplate/formTemplateSection.tsx/formTemplateSection-view';
import { LogIndex } from './pages/log/log-index';
import { LoginForgotPassword } from './pages/login/login-forgotPassword';
import { LoginIndex } from './pages/login/login-index';
import { LoginResetPassword } from './pages/login/login-resetPassword';
import { LoginVerifyCode } from './pages/login/login-verifyCode';
import { AppScreen } from './screens/app-screen';
import { AuthScreen } from './screens/auth-screen';
import { UsersIndex } from './pages/users/users-index';
import { CountyIndex } from './pages/county/county-index';
import { ComplexionIndex } from './pages/complexion/complexion-index';
import { HairColoursIndex } from './pages/hairColours/hairColours-index'
import { EyeColoursIndex } from './pages/eyeColours/eyeColours-index'
import { BodyTypesIndex } from './pages/bodyTypes/bodyTypes-index'
import { EthnicOriginsIndex } from './pages/ethnicOrigins/ethnicOrigins-index'
import { CareStatusIndex } from './pages/careStatus/careStatus-index'
import { ClientContactTypesIndex } from './pages/clientContactTypes/clientContactTypes-index'
import { CentreContactTypeIndex } from './pages/centreContactType/centreContactType-index'
import { GenderIndex } from './pages/gender/gender-index'
import { DeletedClientIndex } from './pages/deletedClient/deletedClient-index'
import { NewsCategoryIndex } from './pages/newsCategory/newsCategory-index'
import { NewsIndex } from './pages/news/news-index'
import { EventIndex } from './pages/event/event-index'
import { CentreIndex } from './pages/centre/centre-index'
import { CentreView } from './pages/centre/centre-view';
import { CentreFormPanel } from './pages/centre/components/centre-formPanel';
import { getThemeForBrand } from './_styles/brand.theme.styles';
import { useEffect, useMemo, useState } from 'react';
import { BrandEnum } from './_common/enum/brand.enum';
import { resolveBrandFromUrl } from './_const/brandOrigin.const';
import { applyBrandFaviconToDocument } from './_helpers/brand.helper';
import { getParentOrigin } from './_helpers/parentOriginHelper';
import { ClientIndex } from './pages/client/client-index';
import { ClientView } from './pages/client/client-view';
import { PolicyDocumentIndex, ResourceDocumentIndex } from './pages/documents/document-index';
import { NewsfeedIndex } from './pages/newsfeed/newsfeed-index';
import { FormApprovalIndex } from './pages/form/formApproval-index';

function BrandedApp({ brand }: { brand: BrandEnum }) {
    const theme = useMemo(() => getThemeForBrand(brand), [brand]);

    return (
        <ThemeProvider theme={theme}>
            <BrowserRouter>

                <Routes>
                    <Route path="/login" element={<AuthScreen />}>
                        <Route path="" element={<LoginIndex brand={brand} />} />
                        <Route path="verifycode/:email" element={<LoginVerifyCode brand={brand} />} />
                        <Route path="forgotpassword" element={<LoginForgotPassword brand={brand} />} />
                        <Route path="resetpassword/:code" element={<LoginResetPassword brand={brand} />} />
                    </Route>

                    <Route path="/" element={<AuthRoute element={<AppScreen brand={brand} />} />}>
                        <Route path="/actionType/*" element={<ActionTypeIndex />} />
                        <Route path="/dashboard" element={<> </>} />
                        <Route path="/logs/*" element={<LogIndex />} />
                        <Route path="/users/*" element={<UsersIndex />} />
                        <Route path="/Forms/:formId" element={<FormPageManage />} />
                        <Route path="/FormApprovals/*" element={<FormApprovalIndex />} />
                        <Route path="/FormTemplates/*" element={<FormTemplateIndex />} />
                        <Route path="/FormTemplateSections/:formTemplateSectionId/*" element={<FormTemplateSectionView />} />
                        <Route path="/County/*" element={<CountyIndex />} />
                        <Route path="/Complexion/*" element={<ComplexionIndex />} />
                        <Route path="/HairColours/*" element={<HairColoursIndex />} />
                        <Route path="/EyeColours/*" element={<EyeColoursIndex />} />
                        <Route path="/BodyType/*" element={<BodyTypesIndex />} />
                        <Route path="/EthnicOrigins/*" element={<EthnicOriginsIndex />} />
                        <Route path="/CareStatus/*" element={<CareStatusIndex />} />
                        <Route path="/ClientContactTypes/*" element={<ClientContactTypesIndex />} />
                        <Route path="/CentreContactType/*" element={<CentreContactTypeIndex />} />
                        <Route path="/Gender/*" element={<GenderIndex />} />
                        <Route path="/DeletedClient/*" element={<DeletedClientIndex />} />
                        <Route path="/NewsCategory/*" element={<NewsCategoryIndex />} />
                        <Route path="/News/*" element={<NewsIndex />} />
                        <Route path="/Event/*" element={<EventIndex />} />
                        <Route path="/Centre/Details/:centreId/forms/:formId" element={<FormPageManage />} />
                        <Route path="/Centre/Details/:centreId/forms" element={<CentreFormPanel />} />
                        <Route path="/Centre/Details/:centreId/*" element={<CentreView />} />
                        <Route path="/Centre/*" element={<CentreIndex />} />
                        <Route path="/Client/Details/:clientId/forms/:formId" element={<FormPageManage />} />
                        <Route path="/Client/Details/:clientId/forms" element={<CentreFormPanel />} />
                        <Route path="/Client/Details/:clientId/*" element={<ClientView />} />
                        <Route path="/Client/*" element={<ClientIndex />} />
                        <Route path="/Newsfeed/*" element={<NewsfeedIndex />} />

                        <Route path="/ResourceDocuments/*" element={<ResourceDocumentIndex />} />
                        <Route path="/PolicyDocuments/*" element={<PolicyDocumentIndex />} />

                    </Route>
                </Routes>
            </BrowserRouter>
        </ThemeProvider>
    );
}

function readBrandFromEnvironment(): BrandEnum {
    if (typeof window === 'undefined') {
        return BrandEnum.DaffodilCare;
    }
    const detected = getParentOrigin();
    if (detected) {
        const matched = resolveBrandFromUrl(detected);
        if (matched) {
            return matched;
        }
    }
    return BrandEnum.DaffodilCare;
}

function App() {
    const [activeBrand, setActiveBrand] = useState<BrandEnum>(readBrandFromEnvironment);

    useEffect(() => {
        setActiveBrand(readBrandFromEnvironment());
    }, []);

    useEffect(() => {
        function onMessage(e: MessageEvent) {
            if (e.data && e.data.type === "host-origin") {
                const raw = e.data.origin || e.origin;
                const matched = resolveBrandFromUrl(raw);
                if (matched) setActiveBrand(matched);
            } else if (e.origin) {
                const matched = resolveBrandFromUrl(e.origin);
                if (matched) setActiveBrand(matched);
            }
        }

        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, []);

    useEffect(() => {
        applyBrandFaviconToDocument(activeBrand);
    }, [activeBrand]);

    return <BrandedApp brand={activeBrand} />;
}

export default App;
